import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { useViewMode } from '@/lib/view-mode-context';
import { useUserSettings } from '@/lib/user-settings-context';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useHaptics } from '@/hooks/use-haptics';
import { Check, TrendingUp, ArrowLeft, Image, FileText, Table2, Loader2, Share2, ArrowRight } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import confetti from 'canvas-confetti';
import { sounds } from '@/lib/sounds';
import { minimiseTransactions, calculateExpenseNets, calculateExpensePaidMap, calculateExpenseShareMap } from '@/lib/settlement';
import {
  buildPivotXLSX,
  buildDetailedXLSX,
  buildSummaryImageBlob,
  shareFile,
  type ShareSession,
  type PlayerTotal,
} from '@/lib/settlement-share';
import { format } from 'date-fns';
import { formatSettlementSummary, shareToWhatsApp } from '@/lib/whatsapp-share';
import { PlayerAvatar } from '@/components/PlayerAvatar';

export default function Settlement() {
  const { user } = useAuth();
  const { targetUserId, isReadOnly } = useViewMode();
  const { settings } = useUserSettings();
  const { toast } = useToast();
  const haptics = useHaptics();
  const [playerTotals, setPlayerTotals] = useState<PlayerTotal[]>([]);
  const [sessionIds, setSessionIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);
  const [sharing, setSharing] = useState<'summary-xlsx' | 'image' | 'detailed-xlsx' | 'whatsapp' | null>(null);
  const [totalSettledVolume, setTotalSettledVolume] = useState(0);

  useEffect(() => {
    if (!targetUserId) return;

    const fetchData = async () => {
      // Fetch total settled volume
      const { data: settledSessions } = await supabase
        .from('sessions')
        .select('id')
        .eq('created_by', targetUserId)
        .eq('status', 'settled');
      
      const settledIds = (settledSessions || []).map(s => s.id);
      let settledVol = 0;
      if (settledIds.length > 0) {
        const { data: settledSp } = await supabase
          .from('session_players')
          .select('net_amount')
          .in('session_id', settledIds);
        
        settledVol = (settledSp || []).reduce((sum, sp) => sum + (sp.net_amount && sp.net_amount > 0 ? sp.net_amount : 0), 0);
      }
      setTotalSettledVolume(settledVol);

      // Fetch unsettled data
      const { data: sessions } = await supabase
        .from('sessions')
        .select('id')
        .eq('created_by', targetUserId)
        .eq('status', 'unsettled');

      const ids = (sessions || []).map((s) => s.id);
      setSessionIds(ids);

      const { data: expenses } = await supabase
        .from('expenses')
        .select(`
          id,
          amount,
          payer_id,
          payer:players!payer_id(name, avatar_url),
          participants:expense_participants(
            player_id,
            share,
            player:players(name, avatar_url)
          )
        `)
        .eq('created_by', targetUserId)
        .eq('is_settled', false);

      const totals: Record<string, PlayerTotal> = {};

      if (ids.length > 0) {
        const { data: spData } = await supabase
          .from('session_players')
          .select('net_amount, players(name, avatar_url)')
          .in('session_id', ids);

        for (const sp of spData || []) {
          const p = (sp as any).players;
          const name = p?.name || 'Unknown';
          const avatar_url = p?.avatar_url;
          if (!totals[name]) totals[name] = { name, net: 0, avatar_url };
          
          const netAmount = Number(sp.net_amount || 0);
          totals[name].net = Math.round((totals[name].net + netAmount) * 100) / 100;
        }
      }

      const expenseNets = calculateExpenseNets(expenses || [], (exp, type, part) => {
        const p = type === 'payer' ? (exp as any).payer : (part as any).player;
        return p?.name || 'Unknown';
      });

      // Capture avatars from expenses for players not in sessions
      for (const exp of expenses || []) {
        const p = (exp as any).payer;
        if (p?.name && !totals[p.name]) {
          totals[p.name] = { name: p.name, net: 0, avatar_url: p.avatar_url };
        }
        for (const part of (exp as any).participants || []) {
          const pp = (part as any).player;
          if (pp?.name && !totals[pp.name]) {
            totals[pp.name] = { name: pp.name, net: 0, avatar_url: pp.avatar_url };
          }
        }
      }

      for (const [name, net] of Object.entries(expenseNets)) {
        if (!totals[name]) totals[name] = { name, net: 0 };
        totals[name].net = Math.round((totals[name].net + net) * 100) / 100;
      }

      setPlayerTotals(Object.values(totals).sort((a, b) => b.net - a.net));
      setLoading(false);
    };

    fetchData();
  }, [targetUserId]);

  const settle = async () => {
    if (!user) return;
    if (sessionIds.length === 0 && playerTotals.length === 0) return;
    setSettling(true);

    if (sessionIds.length > 0) {
      await supabase.from('sessions').update({ status: 'settled' }).in('id', sessionIds);
    }

    await supabase
      .from('expenses')
      .update({ is_settled: true })
      .eq('created_by', user.id)
      .eq('is_settled', false);

    await supabase.from('settlements').insert({
      session_ids: sessionIds,
      created_by: user.id,
    });

    if (settings.extra_juice) {
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#9b87f5', '#7E69AB', '#D6BCFA', '#1EAEDB', '#33C3F0']
      });
      haptics.success();
      sounds.play('chaching');
    }

    toast({ title: 'Settled!', description: 'All unsettled sessions and expenses marked as settled.' });
    setSessionIds([]);
    setPlayerTotals([]);
    setSettling(false);
  };

  /* ── Fetch per-session data for sharing ─────────────────── */
  const fetchShareSessions = async (): Promise<ShareSession[]> => {
    if (sessionIds.length === 0) return [];

    const { data: sessionsData } = await supabase
      .from('sessions')
      .select('id, date, buy_in_unit, notes')
      .in('id', sessionIds)
      .order('date', { ascending: true });

    const { data: spData } = await supabase
      .from('session_players')
      .select('session_id, player_id, buy_ins, cash_out, net_amount, players(name)')
      .in('session_id', sessionIds);

    const { data: expensesData } = await supabase
      .from('expenses')
      .select(`
        id, session_id, amount, description, payer_id,
        payer:players!payer_id(name),
        participants:expense_participants(player_id, share, player:players(name))
      `)
      .in('session_id', sessionIds);

    return (sessionsData || []).map((s) => {
      const sessionSp = (spData || []).filter((sp) => sp.session_id === s.id);
      const sessionExp = (expensesData || []).filter((e) => e.session_id === s.id);

      const expensePaidMap = calculateExpensePaidMap(sessionExp);
      const expenseShareMap = calculateExpenseShareMap(sessionExp);

      return {
        date: s.date,
        notes: s.notes,
        players: sessionSp.map((sp: any) => ({
          name: sp.players?.name || 'Unknown',
          buyIns: sp.buy_ins || 0,
          buyInAmount: (sp.buy_ins || 0) * (s.buy_in_unit || 0),
          cashOut: sp.cash_out || 0,
          net: sp.net_amount || 0,
          expensePaid: expensePaidMap[sp.player_id] || 0,
          expenseShare: expenseShareMap[sp.player_id] || 0,
        })),
        expenses: sessionExp.map((e: any) => ({
          description: e.description || 'Expense',
          amount: Number(e.amount),
          payerName: e.payer?.name || 'Unknown',
          participants: (e.participants || []).map((p: any) => ({
            playerName: p.player?.name || 'Unknown',
            share: Number(p.share),
          })),
        })),
      } satisfies ShareSession;
    });
  };

  const handleShareSummaryXLSX = async () => {
    setSharing('summary-xlsx');
    try {
      const sessions = await fetchShareSessions();
      const blob = await buildPivotXLSX(sessions);
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      await shareFile(blob, `ssc-settlement-${dateStr}.xlsx`);
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Export failed', description: err.message });
    } finally {
      setSharing(null);
    }
  };

  const handleShareImage = async () => {
    setSharing('image');
    try {
      const sessions = await fetchShareSessions();
      const dateLabel = format(new Date(), 'd MMM yyyy');
      const blob = await buildSummaryImageBlob(sessions, playerTotals, dateLabel);
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      await shareFile(blob, `ssc-settlement-${dateStr}.png`);
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Image generation failed', description: err.message });
    } finally {
      setSharing(null);
    }
  };

  const handleShareDetailedXLSX = async () => {
    setSharing('detailed-xlsx');
    try {
      const sessions = await fetchShareSessions();
      const blob = await buildDetailedXLSX(sessions);
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      await shareFile(blob, `ssc-settlement-detailed-${dateStr}.xlsx`);
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Export failed', description: err.message });
    } finally {
      setSharing(null);
    }
  };

  const handleShareWhatsAppSummary = () => {
    setSharing('whatsapp');
    try {
      const prefix = isReadOnly ? `/public/${targetUserId}` : '';
      const url = `${window.location.origin}${prefix}/settlement`;
      
      const summary = formatSettlementSummary({
        playerTotals,
        transfers,
        pendingVolume
      }, url);
      
      shareToWhatsApp(summary);
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Share failed', description: err.message });
    } finally {
      setSharing(null);
    }
  };

  if (loading) {
    return (
      <div className="py-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const transfers = minimiseTransactions(playerTotals);
  const pendingVolume = playerTotals.reduce((sum, pt) => sum + (pt.net > 0 ? pt.net : 0), 0);

  return (
    <div className="py-6 space-y-4">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <Link to={isReadOnly ? `/public/${targetUserId}` : '/'}>
          <button type="button" className="p-2 rounded-lg hover:bg-muted transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </button>
        </Link>
        <h1 className="text-xl font-bold tracking-tight">Settlement</h1>
      </div>

      <p className="text-sm text-muted-foreground">
        Aggregate totals across {sessionIds.length} unsettled session{sessionIds.length !== 1 ? 's' : ''}
      </p>

      {/* ── Financial Summary ─────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="border-0 shadow-sm bg-success/5 border-l-4 border-l-success/60">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total Settled</p>
            <p className="text-xl font-bold font-mono text-success">{formatCurrency(totalSettledVolume)}</p>
            <p className="text-[9px] text-muted-foreground mt-1">HISTORICAL VOLUME</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-amber-500/5 border-l-4 border-l-amber-500/60">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Pending</p>
            <p className="text-xl font-bold font-mono text-amber-600">{formatCurrency(pendingVolume)}</p>
            <p className="text-[9px] text-muted-foreground mt-1">CURRENT UNSETTLED</p>
          </CardContent>
        </Card>
      </div>

      {playerTotals.length === 0 ? (
        <div className="text-center py-12 space-y-2">
          <TrendingUp className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="text-muted-foreground">No unsettled sessions.</p>
        </div>
      ) : (
        <>
          {/* ── Player totals ─────────────────────────────── */}
          <div className="space-y-2">
            {playerTotals.map((pt, i) => (
              <Card key={i} className="border-0 shadow-sm">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <PlayerAvatar name={pt.name} avatarUrl={pt.avatar_url} size={32} />
                    <p className="font-semibold">{pt.name}</p>
                  </div>
                  <span
                    className={cn(
                      'text-lg font-bold font-mono',
                      pt.net > 0 ? 'text-success' : pt.net < 0 ? 'text-destructive' : 'text-muted-foreground',
                    )}
                  >
                    {pt.net >= 0 ? '+' : ''}{formatCurrency(pt.net)}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ── How to settle ─────────────────────────────── */}
          {transfers.length > 0 && (
            <Card className="border-0 shadow-sm bg-muted/30">
              <CardContent className="p-4 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  How to settle · {transfers.length} transfer{transfers.length !== 1 ? 's' : ''}
                </p>
                {transfers.map((t, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <PlayerAvatar name={t.from} avatarUrl={t.fromAvatarUrl} size={24} />
                      <span className="font-medium truncate">{t.from}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <PlayerAvatar name={t.to} avatarUrl={t.toAvatarUrl} size={24} />
                      <span className="font-medium truncate">{t.to}</span>
                    </div>
                    <span className="font-bold font-mono text-primary ml-2 shrink-0">{formatCurrency(t.amount)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* ── WhatsApp share buttons ────────────────────── */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Share via WhatsApp
              </p>

              <Button
                variant="default"
                className="w-full gap-2 h-11 justify-start bg-green-600 hover:bg-green-700 text-white border-0"
                onClick={handleShareWhatsAppSummary}
                disabled={sharing !== null || playerTotals.length === 0}
              >
                {sharing === 'whatsapp'
                  ? <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                  : <Share2 className="h-4 w-4 shrink-0" />}
                <div className="text-left">
                  <div className="text-sm font-semibold">WhatsApp Summary</div>
                  <div className="text-[10px] text-green-50 text-opacity-80">Text standings & instructions</div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="w-full gap-2 h-11 justify-start"
                onClick={handleShareSummaryXLSX}
                disabled={sharing !== null || sessionIds.length === 0}
              >
                {sharing === 'summary-xlsx'
                  ? <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                  : <Table2 className="h-4 w-4 shrink-0" />}
                <div className="text-left">
                  <div className="text-sm font-semibold">Summary XLSX</div>
                  <div className="text-[10px] text-muted-foreground">Pivot table · Players × Sessions · Colour coded</div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="w-full gap-2 h-11 justify-start"
                onClick={handleShareImage}
                disabled={sharing !== null || playerTotals.length === 0}
              >
                {sharing === 'image'
                  ? <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                  : <Image className="h-4 w-4 shrink-0" />}
                <div className="text-left">
                  <div className="text-sm font-semibold">Summary Image</div>
                  <div className="text-[10px] text-muted-foreground">View standings directly in WhatsApp</div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="w-full gap-2 h-11 justify-start"
                onClick={handleShareDetailedXLSX}
                disabled={sharing !== null || sessionIds.length === 0}
              >
                {sharing === 'detailed-xlsx'
                  ? <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                  : <FileText className="h-4 w-4 shrink-0" />}
                <div className="text-left">
                  <div className="text-sm font-semibold">Full Detailed XLSX</div>
                  <div className="text-[10px] text-muted-foreground">Gross · Buy-ins · Expenses per player · Colour coded</div>
                </div>
              </Button>
            </CardContent>
          </Card>

          {/* ── Confirm settlement ────────────────────────── */}
          {!isReadOnly && (
            <Button
              className="w-full h-14 text-base font-semibold gap-2"
              onClick={settle}
              disabled={settling}
            >
              <Check className="h-5 w-5" />
              {settling ? 'Settling...' : 'Confirm Settlement'}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
